import { Game } from "../../game.js";
import { Vector2 } from "../../common/vector2.js";
import { Rect } from "../../common/rect.js";
import { UIElement, subtreeHasUnloadedImages } from "./ui_element.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { Easing } from "../../transforms/transform_base.js";

export enum ScrollDirection
{
  UP = "up",
  DOWN = "down",
  LEFT = "left",
  RIGHT = "right"
}

export interface IScrollableContainerOptions
{
  direction?: "vertical" | "horizontal";
  scroll_speed?: number;
  momentum_friction?: number;
  enable_momentum?: boolean;
  enable_touch_scroll?: boolean;
  scroll_sensitivity?: number;
  enable_rubber_band?: boolean;
  rubber_band_resistance?: number;
  rubber_band_max_distance?: number;
  rubber_band_progression?: number;
  viewport_corner_radius?: number;
  // Cache the content in a bitmap and repaint only the viewport on scroll
  // instead of the whole screen (see flushPendingRepaints).
  fast_repaint?: boolean;
}

export class UIScrollableContainer extends UIElement
{
  private content_container!: UIElement;

  private viewport_rect!: Rect;
  private content_size: number = 0;
  private scroll_offset: number = 0;
  private max_scroll_offset: number = 0;
  private is_over_scrolling: boolean = false;

  private options: Required<IScrollableContainerOptions>;

  private scroll_velocity: number = 0;

  private touch_start_pos: Vector2 | null = null;
  private touch_last_pos: Vector2 | null = null;
  private touch_start_time: number = 0;
  private touch_last_time: number = 0;
  private is_scrolling: boolean = false;
  private swipe_direction: ScrollDirection | null = null;

  private snap_targets: number[] = [];
  private scroll_end_callback?: () => void;
  private current_snap_section: number = -1;
  private snap_section_change_callback?: ( new_section: number ) => void;

  private did_scroll = false;

  private static pending_repaints = new Set<UIScrollableContainer>();
  // Per side, in device pixels; bigger content falls back to direct rendering.
  private static readonly MAX_CACHE_SIDE_PX = 4096;
  private static readonly CACHE_RETRY_MS = 250;
  private static readonly CACHE_MAX_RETRIES = 20;

  private cache_canvas: HTMLCanvasElement | null = null;
  private cache_ctx: CanvasRenderingContext2D | null = null;
  private content_dirty = true;
  private cache_retry_at = 0;
  private cache_retries = 0;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: IScrollableContainerOptions = {}
  )
  {
    super( ctx, x, y, width, height );

    this.options =
    {
      direction: options.direction ?? "vertical",
      scroll_speed: options.scroll_speed ?? 1.0,
      momentum_friction: options.momentum_friction ?? 0.95,
      enable_momentum: options.enable_momentum ?? true,
      enable_touch_scroll: options.enable_touch_scroll ?? true,
      scroll_sensitivity: options.scroll_sensitivity ?? 1.0,
      enable_rubber_band: options.enable_rubber_band ?? true,
      rubber_band_resistance: options.rubber_band_resistance ?? 0.3,
      rubber_band_max_distance: options.rubber_band_max_distance ?? 100,
      rubber_band_progression: options.rubber_band_progression ?? 3.5,
      viewport_corner_radius: Math.max( 0, options.viewport_corner_radius ?? 0 ),
      fast_repaint: options.fast_repaint ?? false,
    };

    this.setupViewport();
    this.setupContentContainer();
  }


  public setOnScrollEnd( callback: () => void ): void
  {
    this.scroll_end_callback = callback;
  }

  private setupViewport(): void
  {
    this.viewport_rect = new Rect(
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
  }

  private setupContentContainer(): void
  {
    this.content_container = new ScrollContentContainer(
      this.ctx,
      0,
      0,
      this.viewport_rect.getW(),
      this.viewport_rect.getH(),
      this
    );

    this.addChild( this.content_container, false );
  }




  private snapToNearestTarget( direction: ScrollDirection | null ): void
  {
    if ( this.snap_targets.length === 0 ) return;

    const current = this.scroll_offset;

    for ( let i = 0; i < this.snap_targets.length; i++ )
    {
      const start = this.snap_targets[i];
      const end = this.snap_targets[i + 1] ?? this.max_scroll_offset;
      const range = end - start;

      if ( current >= start && current < end )
      {
        let threshold;

        if ( direction === ScrollDirection.DOWN || direction === ScrollDirection.RIGHT )
        {
          threshold = start + range * ( 2 / 3 );
        } else if ( direction === ScrollDirection.UP || direction === ScrollDirection.LEFT )
        {
          threshold = start + range * ( 1 / 3 );
        } else
        {
          threshold = start + range * 0.5;
        }

        if ( current < threshold )
        {
          this.scrollTo( start, true );
        } else
        {
          this.scrollTo( end, true );
        }
        return;
      }
    }

    const closest = this.snap_targets.reduce( ( a, b ) =>
      Math.abs( b - current ) < Math.abs( a - current ) ? b : a
    );
    this.scrollTo( closest, true );
  }

  public getCurrentSnapSection(): number
  {
    const current = this.scroll_offset;

    if ( this.snap_targets.length === 0 ) return 0;

    if (current < this.snap_targets[0])
    {
      return 0;
    }

    for ( let i = 0; i < this.snap_targets.length - 1; i++ )
    {
      const start = this.snap_targets[i];
      const end = this.snap_targets[i + 1];

      if ( current >= start && current < end )
      {
        return i;
      }
    }
    return this.snap_targets.length - 1;
  }

  public setSnapTargets( targets: number[] ): void
  {
    this.snap_targets = targets.map( ( t ) => Math.max( 0, Math.min( t, this.max_scroll_offset ) ) );
  }

  public setOnSnapSectionChange( callback: ( new_section: number ) => void ): void
  {
    this.snap_section_change_callback = callback;
  }

  private updateScrollOffset( new_offset: number ): void
  {
    const clamped_offset = Math.max( 0, Math.min( new_offset, this.max_scroll_offset ) );
    if ( clamped_offset !== this.scroll_offset )
    {
      this.scroll_offset = clamped_offset;
      this.updateContentPosition();
      this.checkSnapSectionChange();
    }
  }

  private checkSnapSectionChange(): void
  {
    const new_section = this.getCurrentSnapSection();
    if ( new_section !== this.current_snap_section )
    {
      this.current_snap_section = new_section;
      if ( this.snap_section_change_callback )
      {
        this.snap_section_change_callback( new_section );
      }
    }
  }

  public addScrollableChild( child: UIElement ): void
  {
    this.content_container.addChild( child );
    this.content_dirty = true;
    this.recalculateContentHeight();
  }

  public removeScrollableChild( child: UIElement ): void
  {
    this.content_container.removeChild( child );
    this.content_dirty = true;
    this.recalculateContentHeight();
  }

  public setContentSize( size: number ): void
  {
    this.content_dirty = true;
    this.content_size = size;
    const viewport_size = this.options.direction === "vertical" 
      ? this.viewport_rect.getH() 
      : this.viewport_rect.getW();
    this.max_scroll_offset = Math.max( 0, this.content_size - viewport_size );

    this.scroll_offset = Math.max( 0, Math.min( this.scroll_offset, this.max_scroll_offset ) );
    this.updateContentPosition();
    this.checkSnapSectionChange();
  }

  public setContentHeight( height: number ): void
  {
    this.setContentSize( height );
  }

  public setContentWidth( width: number ): void
  {
    this.setContentSize( width );
  }

  private recalculateContentHeight(): void
  {
    this.recalculateContentSize();
  }

  private recalculateContentSize(): void
  {
    let max_extent = 0;

    const content_children = this.content_container.getChildren();
    content_children.forEach( ( child: UIElement ) =>
    {
      const child_rect = child.getRect();
      const child_pos = child.pixelPosImportant();
      
      const child_extent = this.options.direction === "vertical"
        ? child_pos.y + child_rect.getH()
        : child_pos.x + child_rect.getW();
        
      max_extent = Math.max( max_extent, child_extent );
    } );

    const viewport_size = this.options.direction === "vertical" 
      ? this.viewport_rect.getH() 
      : this.viewport_rect.getW();
      
    this.setContentSize( Math.max( max_extent, viewport_size ) );
  }

  private updateContentPosition(): void
  {
    const target_pos = this.options.direction === "vertical"
      ? new Vector2( 0, -this.scroll_offset )
      : new Vector2( -this.scroll_offset, 0 );
    this.content_container.setPixelPos( target_pos, true );
    this.markScrolled();
  }

  public scrollTo( offset: number, animate: boolean = true, duration: number = 300, delay: number = 0 ): void
  {
    const clamped_offset = Math.max( 0, Math.min( offset, this.max_scroll_offset ) );

    if ( animate )
    {
      this.animateScrollTo( clamped_offset, duration, delay );
    } else
    {
      this.scroll_offset = clamped_offset;
      this.updateContentPosition();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
    }
  }

  public getScrollOffset(): number
  {
    return this.scroll_offset;
  }



  private applyRubberBandEffect( delta: number ): number
  {
    if ( !this.options.enable_rubber_band )
    {
      return delta;
    }

    const new_offset = this.scroll_offset + delta;

    if ( new_offset < 0 )
    {
      const distance_beyond = Math.abs( new_offset );
      const max_distance = this.options.rubber_band_max_distance;

      if ( distance_beyond > max_distance )
      {
        return delta * 0.05;
      }

      const progress = distance_beyond / max_distance;
      const geometric_factor = Math.pow( 2, progress * this.options.rubber_band_progression );
      const resistance = 1 - ( 1 / geometric_factor );

      return delta * ( 1 - resistance * this.options.rubber_band_resistance );

    } else if ( new_offset > this.max_scroll_offset )
    {
      const distance_beyond = new_offset - this.max_scroll_offset;
      const max_distance = this.options.rubber_band_max_distance;

      if ( distance_beyond > max_distance )
      {
        return delta * 0.05;
      }

      const progress = distance_beyond / max_distance;
      const geometric_factor = Math.pow( 2, progress * this.options.rubber_band_progression );
      const resistance = 1 - ( 1 / geometric_factor );

      return delta * ( 1 - resistance * this.options.rubber_band_resistance );
    }

    return delta;
  }

  private snapBackToBounds(): void
  {
    if ( !this.options.enable_rubber_band ) return;

    let target_offset = this.scroll_offset;

    if ( this.scroll_offset < 0 )
    {
      target_offset = 0;
    } else if ( this.scroll_offset > this.max_scroll_offset )
    {
      target_offset = this.max_scroll_offset;
    }

    if ( target_offset !== this.scroll_offset )
    {
      this.is_over_scrolling = false;
      this.animateScrollTo( target_offset );
    }
  }

  private animateScrollTo( target_offset: number, duration: number = 300, delay: number = 0 ): void
  {
    if ( Math.abs( target_offset - this.scroll_offset ) < 1 )
    {
      this.scroll_offset = target_offset;
      this.updateContentPosition();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
      return;
    }

    const current_pos = this.options.direction === "vertical"
      ? new Vector2( 0, -this.scroll_offset )
      : new Vector2( -this.scroll_offset, 0 );
    const target_pos = this.options.direction === "vertical"
      ? new Vector2( 0, -target_offset )
      : new Vector2( -target_offset, 0 );

    const move_transform = new TransformStraightMove(
      this.content_container,
      true,
      current_pos,
      target_pos,
      performance.now() + delay,
      duration,
      { easing: Easing.EaseOutQuad }
    );

    move_transform.setOnFinished( () =>
    {
      this.scroll_offset = target_offset;
      this.markScrolled();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
    } );

    this.content_container.addTransform( move_transform );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    this.did_scroll = false;
    const is_in_viewport = this.isInViewport( p );
    const handled = is_in_viewport && super.onTouchStart( p, now );
    const should_capture_touch = handled || is_in_viewport;

    if ( should_capture_touch )
    {
      this.touch_start_pos = p.clone();
      this.touch_last_pos = p.clone();
      this.touch_start_time = now;
      this.touch_last_time = now;
      this.is_scrolling = false;
      this.scroll_velocity = 0;
      return true;
    }

    this.touch_start_pos = null;
    this.touch_last_pos = null;
    this.is_scrolling = false;

    return false;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    if ( this.touch_start_pos && this.options.enable_touch_scroll )
    {
      const delta = p.subtract( this.touch_last_pos! );
      const time_delta = now - this.touch_last_time;

      if ( !this.is_scrolling )
      {
        const total_delta = p.subtract( this.touch_start_pos );
        const scroll_threshold = 10;

        const primary_delta = this.options.direction === "vertical" 
          ? Math.abs( total_delta.y ) 
          : Math.abs( total_delta.x );

        if ( primary_delta > scroll_threshold )
        {
          this.is_scrolling = true;
          this.did_scroll = true;
          // The scroll now owns the gesture. Release any nested button bump,
          // but do not send onTouchEnd because that could trigger its click.
          super.cancelTouch( now );
        }
      }

      if ( this.is_scrolling )
      {
        const primary_delta = this.options.direction === "vertical" ? -delta.y : -delta.x;
        const raw_scroll_delta = primary_delta * this.options.scroll_sensitivity;
        const adjusted_delta = this.applyRubberBandEffect( raw_scroll_delta );

        this.updateScrollOffset( this.scroll_offset + adjusted_delta );

        if ( time_delta > 0 )
        {
          this.scroll_velocity = primary_delta / time_delta;
        }

        this.touch_last_pos = p.clone();
        this.touch_last_time = now;
        return;
      }
    }

    super.onTouchMove( p, now );
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    if ( !this.touch_start_pos )
    {
      return;
    }

    if ( this.touch_start_pos && this.touch_last_pos )
    {
      if ( this.options.direction === "vertical" )
      {
        const deltaY = this.touch_last_pos.y - this.touch_start_pos.y;
        if ( Math.abs( deltaY ) > 10 )
        {
          this.swipe_direction = deltaY > 0 ? ScrollDirection.DOWN : ScrollDirection.UP;
        } else
        {
          this.swipe_direction = null;
        }
      } else
      {
        const deltaX = this.touch_last_pos.x - this.touch_start_pos.x;
        if ( Math.abs( deltaX ) > 10 )
        {
          this.swipe_direction = deltaX > 0 ? ScrollDirection.RIGHT : ScrollDirection.LEFT;
        } else
        {
          this.swipe_direction = null;
        }
      }
    }

    if ( this.snap_targets.length > 0 && !this.is_over_scrolling )
    {
      requestAnimationFrame( () =>
      {
        this.snapToNearestTarget( this.swipe_direction );
      });
    } else
    {
      this.snapBackToBounds();
    }

    this.touch_start_pos = null;
    this.touch_last_pos = null;
    this.is_scrolling = false;
    
    // Always call super.onTouchEnd to ensure child elements (buttons) receive onTouchEnd
    // This is needed to reset is_pressed state even if scrolling occurred
    if ( !this.did_scroll )
    {
      super.onTouchEnd( p, now, p_global );
    }
    else
    {
      // Defensive cleanup for controls pressed immediately before momentum or
      // rubber-band scrolling started.
      super.cancelTouch( now );
    }
  }

  private isInViewport( p: Vector2 ): boolean
  {
    const local_p = this.toLocalCoordinates( p );
    if ( !this.viewport_rect.contains( local_p.x, local_p.y ) )
      return false;

    const radius = Math.min(
      this.options.viewport_corner_radius,
      this.viewport_rect.getW() / 2,
      this.viewport_rect.getH() / 2
    );
    if ( radius <= 0 )
      return true;

    const nearest_x = Math.max(
      radius,
      Math.min( this.viewport_rect.getW() - radius, local_p.x )
    );
    const nearest_y = Math.max(
      radius,
      Math.min( this.viewport_rect.getH() - radius, local_p.y )
    );
    const dx = local_p.x - nearest_x;
    const dy = local_p.y - nearest_y;
    return dx * dx + dy * dy <= radius * radius;
  }

  private toLocalCoordinates( p: Vector2 ): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2( p.x - pixel_pos.x, p.y - pixel_pos.y );
  }

  protected updateBasic( now: number ): void
  {
    super.updateBasic( now );

    if ( this.content_container.isAnimating() )
    {
      const current_pos = this.content_container.pixelPosImportant();
      const new_scroll_offset = this.options.direction === "vertical"
        ? Math.max( 0, -current_pos.y )
        : Math.max( 0, -current_pos.x );
      if ( Math.abs( new_scroll_offset - this.scroll_offset ) > 1 )
      {
        this.scroll_offset = new_scroll_offset;
        this.markScrolled();
        this.checkSnapSectionChange();
      }
    }
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    // A full pass paints this container, so a queued viewport repaint is moot.
    UIScrollableContainer.pending_repaints.delete( this );

    this.startRender();

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.roundRect(
      -this.viewport_rect.getW() / 2,
      -this.viewport_rect.getH() / 2,
      this.viewport_rect.getW(),
      this.viewport_rect.getH(),
      Math.min(
        this.options.viewport_corner_radius,
        this.viewport_rect.getW() / 2,
        this.viewport_rect.getH() / 2
      )
    );
    this.ctx.clip();
    if ( !this.options.fast_repaint || this.dom_mode || !this.drawContentFromCache() )
      this.renderChildren();
    this.ctx.restore();

    this.finishRender();
  }

  public isFastRepaint(): boolean
  {
    return this.options.fast_repaint && !this.dom_mode;
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    if ( node.style.overflow !== "hidden" )
      node.style.overflow = "hidden";
    const radius = Math.min(
      this.options.viewport_corner_radius,
      this.viewport_rect.getW() / 2,
      this.viewport_rect.getH() / 2
    );
    const radius_css = radius > 0 ? `${radius}px` : "";
    if ( node.style.borderRadius !== radius_css )
      node.style.borderRadius = radius_css;
    return true;
  }

  // Something in the content really changed: the cached bitmap is stale.
  public markContentDirty(): void
  {
    this.content_dirty = true;
  }

  // Scrolling only moves the content. With fast_repaint the container queues a
  // clipped repaint of its viewport instead of dirtying the screen tree, which
  // would repaint (and re-raster) the whole screen on every move.
  public requestViewportRepaint(): void
  {
    UIScrollableContainer.pending_repaints.add( this );
  }

  private markScrolled(): void
  {
    if ( this.options.fast_repaint && !this.dom_mode )
      this.requestViewportRepaint();
    else
      this.setShouldBeRendered();
  }

  // Called once per frame after the current screen rendered. A container that
  // scrolled but was not painted by a full screen pass repaints only its
  // viewport: the whole screen tree renders under a clip, so whatever is drawn
  // under or over the list stays correct, the raster work covers just the
  // viewport, and the list itself is one drawImage of the cached content.
  public static flushPendingRepaints( current_root: object | null ): void
  {
    if ( this.pending_repaints.size === 0 )
      return;

    for ( const container of this.pending_repaints )
    {
      // Screens share canvases: a repaint queued by a screen that is no longer
      // shown would paint its stale tree over the current one.
      if ( current_root && container.getRootElement() === current_root )
        container.repaintViewport();
    }
    this.pending_repaints.clear();
  }

  private repaintViewport(): void
  {
    const root = this.getRootElement();

    // Snap the clip to device pixels so its edges do not blend half-covered pixels.
    const dpr = Game.getDpr();
    const abs = this.getAbsolutePosition();
    const x0 = Math.floor( abs.x * dpr ) / dpr;
    const y0 = Math.floor( abs.y * dpr ) / dpr;
    const x1 = Math.ceil( ( abs.x + this.rect.getW() ) * dpr ) / dpr;
    const y1 = Math.ceil( ( abs.y + this.rect.getH() ) * dpr ) / dpr;

    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect( x0, y0, x1 - x0, y1 - y0 );
    ctx.clip();
    root.invalidateSubtree();
    root.render();
    ctx.restore();

    // Nothing else was dirty (the screen would have rendered in full this frame
    // otherwise); drop the flags the clipped pass set so they read as clean.
    root.skipRenderSubtree();  }

  private contentCacheSize(): { w: number; h: number }
  {
    return this.options.direction === "vertical"
      ? { w: this.viewport_rect.getW(), h: Math.max( this.content_size, this.viewport_rect.getH() ) }
      : { w: Math.max( this.content_size, this.viewport_rect.getW() ), h: this.viewport_rect.getH() };
  }

  private drawContentFromCache(): boolean
  {
    const dpr = Game.getDpr();
    const size = this.contentCacheSize();
    const bw = Math.ceil( size.w * dpr );
    const bh = Math.ceil( size.h * dpr );
    const max_side = UIScrollableContainer.MAX_CACHE_SIDE_PX;
    if ( bw <= 0 || bh <= 0 || bw > max_side || bh > max_side )
      return false;

    const stale = this.content_dirty
      || !this.cache_canvas
      || this.cache_canvas.width !== bw
      || this.cache_canvas.height !== bh
      || ( this.cache_retry_at > 0 && performance.now() >= this.cache_retry_at );

    if ( stale && !this.rebuildContentCache( bw, bh, dpr ) )
      return false;

    const pos = this.content_container.pixelPosForRender();
    this.ctx.drawImage(
      this.cache_canvas!,
      -this.rect.getW2() + pos.x,
      -this.rect.getH2() + pos.y,
      size.w,
      size.h
    );

    // The content was not walked; clear the flags an ancestor's invalidation
    // set on it so they do not read as a content change next frame.
    this.content_container.skipRenderSubtree();
    return true;
  }

  private rebuildContentCache( bw: number, bh: number, dpr: number ): boolean
  {
    if ( !this.cache_canvas )
    {
      this.cache_canvas = document.createElement( "canvas" );
      this.cache_ctx = this.cache_canvas.getContext( "2d" );
    }
    const cache_ctx = this.cache_ctx;
    if ( !cache_ctx )
      return false;

    if ( this.cache_canvas.width !== bw || this.cache_canvas.height !== bh )
    {
      this.cache_canvas.width = bw;
      this.cache_canvas.height = bh;
    }
    cache_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    cache_ctx.globalAlpha = 1;
    cache_ctx.clearRect( 0, 0, bw, bh );
    cache_ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );

    // Children render at their content-local positions, so with the origin at
    // the cache's top-left they land where the content container puts them.
    this.content_container.setContextRecursive( cache_ctx );
    try
    {
      for ( const child of this.content_container.getChildren() )
      {
        child.invalidateSubtree();
        child.render();
      }
    }
    finally
    {
      this.content_container.setContextRecursive( this.ctx );
    }
    this.content_container.skipRenderSubtree();

    // Images still loading draw nothing; rebuild a few times until they land.
    const was_retry = !this.content_dirty && this.cache_retry_at > 0;
    this.content_dirty = false;
    this.cache_retries = was_retry ? this.cache_retries + 1 : 0;
    this.cache_retry_at = this.cache_retries < UIScrollableContainer.CACHE_MAX_RETRIES
      && subtreeHasUnloadedImages( this.content_container )
      ? performance.now() + UIScrollableContainer.CACHE_RETRY_MS
      : 0;    return true;
  }

  public name(): string
  {
    return "UIScrollableContainer";
  }
}

class ScrollContentContainer extends UIElement
{
  private readonly owner: UIScrollableContainer;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    owner: UIScrollableContainer
  )
  {
    super( ctx, x, y, width, height );
    this.owner = owner;
  }

  protected override onChildrenUpdated( any_child_dirty: boolean ): void
  {
    if ( !this.owner.isFastRepaint() )
      return;

    if ( any_child_dirty )
    {
      // A card really changed: repaint through the normal tree and rebuild
      // the cached content bitmap.
      this.owner.markContentDirty();
      return;
    }

    // Only moved (scroll animation): repaint the viewport, not the screen.
    if ( this.shouldBeRendered() )
    {
      this.resetShouldBeRendered();
      this.owner.requestViewportRepaint();
    }
  }

  public name(): string
  {
    return "ScrollContentContainer";
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    if ( node.style.willChange !== "transform" )
      node.style.willChange = "transform";
    return true;
  }

  protected containsPointInner( p: Vector2 ): boolean
  {
    // Always return true to allow child elements (buttons) to receive touch events
    // even when they are outside the viewport due to scrolling
    // Child elements will check their own bounds
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
